/**
 * Kiosk Preview Panel
 * Displays live preview of kiosk UI with auto-scaling to fit container
 * Updates automatically when settings change
 */

import { useEffect, useRef, useState } from 'react';
import { KioskPreview } from '@/features/device-customizer/kiosk/components/preview/KioskPreview';
import { useKioskSettingsStore } from '../stores/kiosk-settings-store';
import { useKioskPreviewStore } from '../stores/kiosk-preview-store';

export const KioskPreviewPanel = () => {
  const {
    settings,
    pendingLogo,
    pendingIdleScreen4,
    pendingIdleScreen5,
    showIdleScreenInPreview,
  } = useKioskSettingsStore();
  const { formFactor, getPreviewConfig } = useKioskPreviewStore();
  const previewContainerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  // Get live preview config (recomputes when settings change)
  const config = getPreviewConfig();

  // Get pending logo preview URL (or fallback to saved logo)
  const logoPreviewUrl = pendingLogo?.previewUrl || settings?.logo || undefined;

  // Get pending idle screen based on form factor
  const pendingIdleScreen = formFactor === '4inch' ? pendingIdleScreen4 : pendingIdleScreen5;

  // Force re-render when pending uploads or settings change
  useEffect(() => {
    // This ensures preview updates when logo or idle screens change
  }, [settings?.logo, pendingLogo, pendingIdleScreen4, pendingIdleScreen5]);

  // Auto-scale preview to fit the visible height of closed categories (~566px)
  useEffect(() => {
    if (!previewContainerRef.current) return;

    const calculateScale = () => {
      const container = previewContainerRef.current;
      if (!container) return;

      const containerWidth = container.clientWidth;

      // Fixed target height matching closed categories:
      // Toggle buttons (52px) + 6 categories (68px each) + gaps (16px * 6) = ~566px
      const targetHeight = 566;

      // Get mockup dimensions based on form factor
      const mockupWidth = formFactor === '4inch' ? 480 : 420;
      const mockupHeight = formFactor === '4inch' ? 480 : 720;

      // Calculate scale to fit fixed target height
      const paddingWidth = 20;
      const paddingHeight = 20;

      const scaleWidth = (containerWidth - paddingWidth) / mockupWidth;
      const scaleHeight = (targetHeight - paddingHeight) / mockupHeight;

      // Use the smaller scale to ensure it fits both dimensions
      let newScale = Math.min(scaleWidth, scaleHeight);

      // Minimal clamping
      newScale = Math.max(0.3, Math.min(1.0, newScale));

      setScale(newScale);
    };

    // Initial calculation with delay
    const timer = setTimeout(calculateScale, 100);

    // Recalculate on window resize
    const resizeObserver = new ResizeObserver(() => {
      calculateScale();
    });

    if (previewContainerRef.current) {
      resizeObserver.observe(previewContainerRef.current);
    }

    return () => {
      clearTimeout(timer);
      resizeObserver.disconnect();
    };
  }, [formFactor]);

  return (
    <div className="flex flex-col h-full" dir="ltr">
      {/* Preview Container - Auto-scaled kiosk - Always LTR */}
      <div
        ref={previewContainerRef}
        className="flex-1 flex items-start justify-center bg-gray-100 p-2 pt-4 rounded-lg overflow-hidden"
      >
        <div
          style={{
            transform: `scale(${scale})`,
            transformOrigin: 'top center',
            transition: 'transform 0.2s ease-in-out',
          }}
        >
          <KioskPreview
            config={config}
            connectors={undefined} // Use default connectors from KioskPreview
            pendingLogoUrl={logoPreviewUrl}
            pendingIdleScreen={pendingIdleScreen || undefined}
            showIdleScreen={showIdleScreenInPreview}
          />
        </div>
      </div>
    </div>
  );
};
